// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import {
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import checkoutReducer, { CheckoutReducerAction } from "@keller/App/Widgets/CheckoutStepper/Checkout.reducer";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    generateSteps,
    some,
} from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { doScroll } from "@keller/XDL/Utilities";
import React, { createContext, PropsWithChildren, useReducer, useRef, useState } from "react";
import { shallowEqual } from "react-redux";

interface InitialCheckoutInfoState {
    doNotShipBeforeDate?: Date | undefined;
    [key: string]: any;
}

export const initialCheckoutInfoState: InitialCheckoutInfoState = {
    paymentProfile: "",
    poNumber: "",
    paymentMethod: "",
    cardHolderName: "",
    lastFour: "",
    expirationMonth: 1,
    expirationYear: new Date().getFullYear(),
    vatNumber: "",
    saveCard: false,
    cardNumber: "",
    cardType: "",
    possibleCardType: "",
    securityCode: "",
    useBillingAddress: true,
    address1: "",
    countryId: "",
    stateId: "",
    city: "",
    postalCode: "",
    releaseNumber: "",
    associateName: "",
    phoneNumber: "",
    accountHolderName: "",
    accountNumber: "",
    routingNumber: "",
    firstSix: "",
    savedPaymentDescription: "",
    isSavedPayment: false,
    isCreditCard: false,
    doNotShipBeforeDate: undefined,
    shipComplete: false,
};

export type CheckoutInfoState = Partial<typeof initialCheckoutInfoState>;

export interface StepperContextData {
    steps: CheckoutStep[];
    currentStep: number;
    maxStep: number;
    checkoutInfoState: CheckoutInfoState;
    setCurrentStep: (step: number) => void;
    moveToStepHandler: (step: number) => void;
    setMaxStep: (step: number) => void;
    preventStepperChange: (hook: any, position: number) => void;
    updateCheckoutInfoState: React.Dispatch<CheckoutReducerAction>;
}

export type HasStepperContext = Pick<StepperContextData, "currentStep">;

const stepperContextData: StepperContextData = {
    steps: [],
    currentStep: 1,
    maxStep: 1,
    checkoutInfoState: {},
    setCurrentStep: (step: number) => {},
    moveToStepHandler: (step: number) => {},
    setMaxStep: (step: number) => {},
    preventStepperChange: (hook: any, position: number) => {},
    updateCheckoutInfoState: () => {},
};

export const StepperContext = createContext<StepperContextData>(stepperContextData);

const StepperContextProvider: React.FC<PropsWithChildren<{ initialStep?: number }>> = ({
    initialStep = 1,
    children,
}) => {
    const history = useHistory();
    const cartId = useAppSelector(state => state.pages.checkoutReviewAndSubmit.cartId);
    const cartState = useAppSelector(
        state => (cartId ? getCartState(state, cartId) : getCurrentCartState(state)),
        shallowEqual,
    );
    const checkoutShippingPage = useAppSelector(
        state => getPageLinkByPageType(state, "CheckoutShippingPage"),
        shallowEqual,
    );
    const cartPage = useAppSelector(state => getPageLinkByPageType(state, "CartPage"), shallowEqual);
    const isPickUp = cartState.value?.fulfillmentMethod === FulfillmentMethod.PickUp;
    const requiresApproval = useAppSelector(state => canSubmitForApprovalOrder(getCurrentCartState(state).value));
    const orderConfirmationCart = useAppSelector(
        state => getCartState(state, state.pages.orderConfirmation.cartId).value,
        shallowEqual,
    );

    const isPlacingOrder = useAppSelector(state => state.pages.checkoutReviewAndSubmit.isPlacingOrder);
    const stepsRef = useRef<CheckoutStep[]>([]);
    if (!isPlacingOrder) {
        stepsRef.current = generateSteps(isPickUp, requiresApproval);
    }
    const steps = stepsRef.current;
    const [currentStep, doSetCurrentStep] = useState<number>(initialStep);
    const [maxStep, doSetMaxStep] = useState<number>(initialStep);
    const [checkoutInfoState, updateCheckoutInfoState] = useReducer(checkoutReducer, initialCheckoutInfoState);
    const validationHookRef = useRef<any[]>([]);

    React.useEffect(() => {
        doSetCurrentStep(initialStep);
    }, [initialStep]);

    React.useEffect(() => {
        if (!orderConfirmationCart || !orderConfirmationCart.id) {
            return;
        }
        const [expirationMonth, expirationYear] = (orderConfirmationCart?.properties?.expirationDate || "").split("/");
        updateCheckoutInfoState({
            payload: {
                cardType: orderConfirmationCart?.properties?.cardType,
                lastFour: orderConfirmationCart?.properties?.lastFour,
                expirationMonth: +expirationMonth,
                expirationYear: +expirationYear,
                paymentMethod:
                    orderConfirmationCart?.paymentMethod?.description || orderConfirmationCart?.paymentMethod?.name,
                savedPaymentDescription: orderConfirmationCart?.paymentMethod?.description,
                isCreditCard: orderConfirmationCart?.paymentMethod?.isCreditCard,
                isSavedPayment: orderConfirmationCart?.paymentMethod?.isPaymentProfile,
                poNumber: orderConfirmationCart?.poNumber || orderConfirmationCart?.properties.defaultPONumber,
                releaseNumber:
                    orderConfirmationCart?.properties.releaseNumber ??
                    orderConfirmationCart?.properties.defaultReleaseNumber,
                doNotShipBeforeDate: orderConfirmationCart?.properties.doNotShipBeforeDate
                    ? new Date(orderConfirmationCart?.properties.doNotShipBeforeDate)
                    : undefined,
            },
            type: "update",
        });
    }, [orderConfirmationCart?.id, orderConfirmationCart?.poNumber, orderConfirmationCart?.properties?.releaseNumber]);

    const setMaxStep = (state: number) => {
        doSetMaxStep(state);
    };

    // To update currentStep value to nextStep step
    const setCurrentStep = async (nextStep: number) => {
        const prevent = await some(validationHookRef.current, (item: any) => {
            const hook = item.hook.current(currentStep, nextStep);

            // check is already a promise and return
            if (typeof hook?.then === "function") {
                return hook;
            }
            return Promise.resolve(!!hook);
        });

        if (prevent) {
            return;
        }

        if (currentStep > maxStep) {
            setMaxStep(currentStep);
        }
        doSetCurrentStep(nextStep);
    };

    const preventStepperChange = (hook: any, position = 1) => {
        const vh = validationHookRef.current;
        const index = vh.findIndex((f: any) => f.position === position);
        if (index !== -1) {
            vh.splice(index, 1);
        }
        vh.push({ hook, position });
        vh.sort((a, b) => a.position - b.position);
    };

    // To move to any step
    const moveToStep = (moveToNewStep: number) => {
        if (moveToNewStep >= currentStep) {
            return;
        }

        if (moveToNewStep === CHECKOUT_STEPS.CART) {
            history.push(`${cartPage?.url}`);
        } else if (moveToNewStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(`${checkoutShippingPage?.url}`);
        }

        setCurrentStep?.(moveToNewStep);
        doScroll(true);
    };

    return (
        <StepperContext.Provider
            value={{
                steps,
                currentStep,
                setCurrentStep,
                maxStep,
                checkoutInfoState,
                setMaxStep,
                preventStepperChange,
                moveToStepHandler: moveToStep,
                updateCheckoutInfoState,
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export default StepperContextProvider;

export function withStepperContext<P extends HasStepperContext>(Component: React.ComponentType<P>) {
    return function StepperComponent(props: Omit<P, keyof HasStepperContext>) {
        return (
            <StepperContext.Consumer>
                {value => <Component {...(props as P)} currentStep={value.currentStep} />}
            </StepperContext.Consumer>
        );
    };
}
